import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logo, background } from "../../assets";
import axios from "../../library/axios";
import { MySwal } from "../../library/swal";
import { IconLogin, IconLock, IconShieldLock, IconUser } from "@tabler/icons-react";
import { setCpanelToken, setCentralUser, handleCpanelLogoutQuery } from "../../helpers/cpanelAuth";
import { APP_VERSION } from "../../constants/appVersion";
import { useCpanelDocumentTitle } from "../../helpers/useCpanelDocumentTitle";
import ResetPasswordModal from "../../components/auth/ResetPasswordModal";
import "./cpanel-login.css";
import "../../components/cpanel/cpanel-buttons.css";
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';

const GUIDE_URL =
  "https://drive.google.com/drive/folders/1Lb00mSP0m3xDpeooCMSYS2oCNySr8MGF?usp=sharing";
const DEFAULT_RESET_PASSWORD = "Qwerty123#";

const CpanelLoginPage = () => {
  useCpanelDocumentTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ username: "", password: "" });
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [loadingReset, setLoadingReset] = useState(false);

  useEffect(() => {
    handleCpanelLogoutQuery();

    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');
    if (ssoError) {
      MySwal.fire({
        icon: 'error',
        title: 'Gagal masuk otomatis',
        text: ssoError,
      });
      params.delete('error');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
    }
  }, []);

  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const validateForm = useCallback(() => {
    const errors = {
      username: !username.trim() ? "Username wajib diisi." : "",
      password: !password ? "Password wajib diisi." : "",
    };

    setFieldErrors(errors);
    return !errors.username && !errors.password;
  }, [username, password]);

  const handleLogin = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setDisabled(true);

    axios({
      url: "/cpanel/auth",
      method: "POST",
      data: { username: username.trim(), password },
    })
      .then((res) => {
        if (res.status === 200 && res.data?.token) {
          setCpanelToken(res.data.token);
          if (res.data.user) {
            setCentralUser(res.data.user);
          }

          MySwal.fire({
            icon: "success",
            title: "Berhasil Login!",
            text: "Mengalihkan ke panel administrasi…",
            timer: 1200,
            showConfirmButton: false,
          }).then(() => {
            navigate("/cpanel/dashboard", { replace: true });
          });

          setLoading(false);
          setDisabled(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        setDisabled(false);
        MySwal.fire({
          icon: "error",
          title: "Oops, Error!",
          text: getApiErrorMessage(err, "Username atau password salah"),
        });
      });
  }, [username, password, navigate, validateForm]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !disabled && !loading) {
      handleLogin();
    }
  };

  const openResetModal = () => {
    setResetUsername(username.trim());
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    if (loadingReset) return;
    setShowResetModal(false);
    setResetUsername("");
  };

  const handleResetPassword = () => {
    const nextUsername = resetUsername.trim();
    if (!nextUsername) {
      MySwal.fire({
        icon: "warning",
        title: "Oops!",
        text: "Username wajib diisi",
      });
      return;
    }

    setLoadingReset(true);
    axios({
      url: "/cpanel/auth/reset-pass",
      method: "POST",
      data: { username: nextUsername },
    })
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: "Berhasil!",
          text: `Password berhasil direset ke ${DEFAULT_RESET_PASSWORD}`,
        });
        setUsername(nextUsername);
        setPassword("");
        setShowResetModal(false);
        setResetUsername("");
      })
      .catch((err) => {
        MySwal.fire({
          icon: "error",
          title: "Oops, Error!",
          text: getApiErrorMessage(err, "Gagal mereset password"),
        });
      })
      .finally(() => {
        setLoadingReset(false);
      });
  };

  return (
    <div className="container-fluid cpanel-login p-0">
      <div className="row g-0 min-vh-100">
        <div className="col-lg-6 cpanel-login__brand d-flex align-items-center justify-content-center py-5 px-4">
          <div className="cpanel-login__brand-inner">
            <img alt="LAM-KPRS" src={logo} className="cpanel-login__logo" />

            <header className="cpanel-login__headings text-center">
              <p className="cpanel-login__welcome">Selamat Datang di</p>
              <h1 className="cpanel-login__title">
                S&nbsp;P&nbsp;E&nbsp;A&nbsp;K&nbsp;&nbsp;H&nbsp;O&nbsp;S&nbsp;P&nbsp;I&nbsp;T&nbsp;A&nbsp;L
              </h1>
              <p className="cpanel-login__subtitle">
                Sistem Pengelolaan Akreditasi LAM-KPRS
              </p>
            </header>

            <div className="cpanel-login__intro">
              <p className="cpanel-login__intro-desc">
                <strong>SPEAK HOSPITAL</strong> adalah platform digital terintegrasi yang
                dikembangkan oleh <strong>LAM-KPRS</strong> untuk mendukung proses akreditasi
                Rumah Sakit dan kegiatan Surveior secara efisien, terdokumentasi, dan transparan.
              </p>
              <p className="cpanel-login__intro-guide">
                Untuk panduan instalasi dan penggunaan web aplikasi silahkan klik{" "}
                <a
                  href={GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cpanel-login__intro-link"
                >
                  link petunjuk berikut
                </a>
                .
              </p>
              <hr className="cpanel-login__intro-divider" />
              <p className="cpanel-login__intro-quote">
                Bersama menuju mutu pelayanan rumah sakit yang unggul melalui akreditasi
                terstandarisasi.
              </p>
            </div>
          </div>
        </div>

        <div
          className="col-lg-6 cpanel-login__form-side d-flex align-items-center justify-content-center p-4 p-lg-5"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="cpanel-login__card-wrap w-100 d-flex justify-content-center">
            <div className="cpanel-login__card bg-body">
              <div className="cpanel-login__card-accent" />
              <div className="cpanel-login__card-body">
                <div className="cpanel-login__form-header">
                  <span className="cpanel-login__badge">
                    <IconShieldLock size={14} stroke={2} />
                    CPanel Center
                  </span>
                  <h2 className="cpanel-login__form-title">Masuk CPanel Center</h2>
                  <p className="cpanel-login__form-subtitle">
                    Kelola tenant, data, dan akses rumah sakit
                  </p>
                </div>

                <div className="fv-row mb-4">
                  <label className="form-label fs-6 fw-bolder text-dark" htmlFor="central-username">
                    Username
                  </label>
                  <div className="cpanel-login__input-wrap">
                    <IconUser size={18} className="cpanel-login__input-icon" />
                    <input
                      id="central-username"
                      className={`form-control form-control-lg form-control-solid${
                        fieldErrors.username ? " is-invalid" : ""
                      }`}
                      type="text"
                      name="username"
                      autoComplete="username"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        clearFieldError("username");
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={disabled}
                      placeholder="Masukkan username"
                    />
                  </div>
                  {fieldErrors.username ? (
                    <div className="cpanel-login__field-error">{fieldErrors.username}</div>
                  ) : null}
                </div>

                <div className="fv-row mb-3">
                  <label className="form-label fw-bolder text-dark fs-6 mb-2" htmlFor="cpanel-password">
                    Password
                  </label>
                  <div className="cpanel-login__input-wrap">
                    <IconLock size={18} className="cpanel-login__input-icon" />
                    <input
                      id="cpanel-password"
                      className={`form-control form-control-lg form-control-solid${
                        fieldErrors.password ? " is-invalid" : ""
                      }`}
                      type="password"
                      name="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFieldError("password");
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={disabled}
                      placeholder="Masukkan password"
                    />
                  </div>
                  {fieldErrors.password ? (
                    <div className="cpanel-login__field-error">{fieldErrors.password}</div>
                  ) : null}
                </div>

                <div className="text-end mb-8">
                  <button
                    type="button"
                    className="cpanel-login__forgot"
                    onClick={openResetModal}
                    disabled={disabled}
                  >
                    Lupa Password?
                  </button>
                </div>

                <button
                  type="button"
                  className="btn btn-primary cpanel-login__submit w-100"
                  onClick={handleLogin}
                  disabled={disabled}
                >
                  <IconLogin size={20} stroke={2} />
                  {loading ? "Mohon tunggu…" : "Masuk"}
                </button>

                <div className="cpanel-login__footer">
                  <span className="cpanel-login__version">Version: {APP_VERSION}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResetPasswordModal
        show={showResetModal}
        username={resetUsername}
        loading={loadingReset}
        onUsernameChange={setResetUsername}
        onClose={closeResetModal}
        onSubmit={handleResetPassword}
      />
    </div>
  );
};

export default CpanelLoginPage;
